import React, { useEffect, useState } from "react";
import { Button, Space, Tag, Typography } from "antd";
import { ArrowLeftOutlined, LoadingOutlined } from "@ant-design/icons";
import AppShell, { AutoSyncBadge } from "Components/Layout/AppShell";
import ChangeView from "Shared/Statistics";
import * as assetAction from "store/Actions/asset";
import { useDispatch, useSelector } from "react-redux";
import { nanoid } from "nanoid";
import { useHistory, useParams } from "react-router-dom";
import { getCoinInfo } from "utils/CoinGecko";
import { getFormattedAmount } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";
import _ from "lodash";
import { performBackup } from "utils/Firebase/backup";
import { updateLastBackup } from "store/Actions/settings";
import {
    mpTapAddTransaction,
    mpTapGoBack,
} from "Shared/Mixpanel/AssetsManager";

const { Title, Text } = Typography;

const Result = () => {
    const dispatch = useDispatch();
    const { type } = useParams();
    const history = useHistory();
    const selectedPortfolio = useSelector(
        (state) => state.portfolios?.selectedPortfolio || {}
    );
    const asset = useSelector((state) => state.assets?.selectedAsset || {});
    const portfolio = useSelector((state) => state.assets?.holdMyResult || {});

    const portfolios = useSelector((state) => state.portfolios?.portfolios);
    const assets = useSelector((state) => state.assets?.assets);
    const userInfo = useSelector((state) => state.users?.userInfo);
    const settings = useSelector((state) => state.settings || {});
    const displayCurrency = settings?.currency || "INR";
    const currSymbol = getCurrencySymbol(displayCurrency);

    const [loading, setLoading] = useState(true);
    const [coinInfo, setCoinInfo] = useState({});

    const handleAddTransaction = async () => {
        const transaction = {
            createdAt: new Date(),
            portfolioSnapshot: portfolio,
            type,
            uid: nanoid(5),
        };
        dispatch(assetAction.addTransaction(transaction, portfolio));
        if (userInfo) {
            const now = Date.now();
            dispatch(updateLastBackup(now));
            const updatedAssets = Array.isArray(assets)
                ? assets.map((item) =>
                      item.uid === asset.uid
                          ? {
                                ...asset,
                                transactions: asset.transactions
                                    ? [...asset.transactions, transaction]
                                    : [transaction],
                                portfolio,
                            }
                          : item
                  )
                : [];
            await performBackup(
                "users",
                userInfo,
                portfolios,
                updatedAssets,
                { ...settings, lastBackup: now },
                { silent: true }
            );
        }
        mpTapAddTransaction({ type });
        history.push(
            `/portfolios/${selectedPortfolio.name}/assets/${asset.id}`
        );
    };

    const onClose = () => {
        history.push(
            `/portfolios/${selectedPortfolio.name}/assets/${asset.id}`
        );
    };

    const getCoin = async () => {
        setLoading(true);
        const res = await getCoinInfo([asset], displayCurrency);
        setCoinInfo((res && res[0]) || {});
        setLoading(false);
    };

    useEffect(() => {
        if (asset && !_.isEmpty(asset)) getCoin();
        // eslint-disable-next-line
    }, []);

    const isBuy = String(type || "").toLowerCase() === "buy";
    const profitColor = Tokens.profit || Tokens.positive;
    const profitBg = Tokens.profitBg || Tokens.positiveBg;
    const profitBorder = Tokens.profitBorder || Tokens.positiveBorder;
    const lossColor = Tokens.loss || Tokens.negative;
    const lossBg = Tokens.lossBg || Tokens.negativeBg;
    const lossBorder = Tokens.lossBorder || Tokens.negativeBorder;

    const prevCoins = Number(portfolio.previous?.numberOfCoins || 0);
    const nextCoins = Number(portfolio.latest?.numberOfCoins || 0);
    const coinDelta = Math.abs(nextCoins - prevCoins);
    const execPrice = Number(
        portfolio.transactionMarketPrice || portfolio.latest?.marketPrice || 0
    );

    const sectionTitle = (label) => (
        <span
            style={{
                color: Tokens.textSecondary,
                fontSize: "12px",
                fontWeight: 500,
            }}
        >
            {label}
        </span>
    );

    return (
        <div>
            <AppShell
                activeTab="overview"
                title={
                    <div
                        style={{
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                minWidth: 0,
                            }}
                        >
                            <Button
                                type="text"
                                icon={
                                    <ArrowLeftOutlined
                                        style={{
                                            color: Tokens.textSecondary,
                                            fontSize: "16px",
                                        }}
                                    />
                                }
                                onClick={onClose}
                                title="Back"
                                style={{
                                    padding: 0,
                                    width: "40px",
                                    height: "40px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            />
                            <Title
                                level={4}
                                style={{
                                    color: Tokens.textPrimary,
                                    margin: 0,
                                    fontWeight: 500,
                                    fontSize: "18px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                Preview {isBuy ? "buy" : "sell"} · {asset.name}
                            </Title>
                            <AutoSyncBadge />
                        </div>
                        <Tag
                            style={{
                                backgroundColor: isBuy ? profitBg : lossBg,
                                color: isBuy ? profitColor : lossColor,
                                border: `1px solid ${
                                    isBuy ? profitBorder : lossBorder
                                }`,
                                borderRadius: Tokens.radiusTag,
                                fontFamily: Tokens.fontMono,
                                fontWeight: 500,
                                fontSize: "11px",
                                margin: 0,
                                flexShrink: 0,
                            }}
                        >
                            {isBuy ? "Buy" : "Sell"}
                        </Tag>
                    </div>
                }
                placement="right"
                closable={false}
                width="100%"
                height="100%"
                push={false}
                onClose={onClose}
                open
                headerStyle={{
                    backgroundColor: Tokens.bg,
                    borderBottom: `1px solid ${Tokens.hairline}`,
                    padding: "16px 24px",
                }}
                bodyStyle={{
                    backgroundColor: Tokens.bg,
                    padding: "24px 16px 80px",
                }}
                footerStyle={{
                    backgroundColor: Tokens.card,
                    borderTop: `1px solid ${Tokens.hairline}`,
                    padding:
                        "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
                }}
                footer={
                    <div
                        style={{
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                        }}
                    >
                        {!loading && (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: "12px",
                                }}
                            >
                                <Button
                                    style={{
                                        backgroundColor: Tokens.raised,
                                        color: Tokens.textPrimary,
                                        border: `1px solid ${Tokens.hairline}`,
                                        borderRadius: Tokens.radiusControl,
                                        height: "40px",
                                    }}
                                    onClick={() => {
                                        mpTapGoBack({
                                            screen: "RESULTS_SCREEN",
                                        });
                                        onClose();
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    style={{
                                        flex: 1,
                                        borderRadius: Tokens.radiusControl,
                                        fontWeight: 500,
                                        height: "40px",
                                    }}
                                    onClick={handleAddTransaction}
                                >
                                    Add Transaction
                                </Button>
                            </div>
                        )}
                    </div>
                }
            >
                <div
                    style={{
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        width: "100%",
                    }}
                >
                    <div
                        style={{
                            backgroundColor: Tokens.card,
                            border: `1px solid ${Tokens.hairline}`,
                            borderRadius: Tokens.radiusCard,
                            padding: "16px 20px",
                            marginBottom: "12px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "12px",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                    marginBottom: "4px",
                                }}
                            >
                                Transaction summary
                            </div>
                            <Text
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "15px",
                                    fontWeight: 500,
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {isBuy ? "+" : "-"}
                                {getFormattedAmount(coinDelta, true, 4)}{" "}
                                {asset.symbol
                                    ? asset.symbol.toUpperCase()
                                    : asset.name}
                            </Text>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <div
                                style={{
                                    color: Tokens.textSecondary,
                                    fontSize: "12px",
                                    marginBottom: "4px",
                                }}
                            >
                                Execution price
                            </div>
                            <Text
                                style={{
                                    color: Tokens.textPrimary,
                                    fontSize: "15px",
                                    fontWeight: 500,
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {currSymbol}
                                {getFormattedAmount(execPrice, true, 2)}
                            </Text>
                        </div>
                    </div>

                    <Space
                        direction="vertical"
                        size={12}
                        style={{
                            textAlign: "left",
                            width: "100%",
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "16px 20px",
                            }}
                        >
                            {!loading ? (
                                <ChangeView
                                    title={sectionTitle("Overall Value")}
                                    currencySymbol={currSymbol}
                                    previousValue={
                                        (portfolio.previous?.numberOfCoins ||
                                            0) *
                                        (coinInfo.current_price ||
                                            execPrice ||
                                            0)
                                    }
                                    latestValue={
                                        (portfolio.latest?.numberOfCoins || 0) *
                                        (coinInfo.current_price ||
                                            execPrice ||
                                            0)
                                    }
                                />
                            ) : (
                                <span
                                    style={{
                                        color: Tokens.textSecondary,
                                        fontSize: "13px",
                                    }}
                                >
                                    Calculating <LoadingOutlined />
                                </span>
                            )}
                        </div>

                        <div
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "16px 20px",
                            }}
                        >
                            <ChangeView
                                title={sectionTitle("Coins")}
                                previousValue={
                                    portfolio.previous?.numberOfCoins || 0
                                }
                                latestValue={
                                    portfolio.latest?.numberOfCoins || 0
                                }
                            />
                        </div>

                        <div
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "16px 20px",
                            }}
                        >
                            <ChangeView
                                title={sectionTitle("Average buy price")}
                                currencySymbol={currSymbol}
                                previousValue={
                                    portfolio.previous?.marketPrice || 0
                                }
                                latestValue={portfolio.latest?.marketPrice || 0}
                                isReverseSentiment
                            />
                        </div>

                        <div
                            style={{
                                backgroundColor: Tokens.card,
                                border: `1px solid ${Tokens.hairline}`,
                                borderRadius: Tokens.radiusCard,
                                padding: "16px 20px",
                            }}
                        >
                            <ChangeView
                                title={sectionTitle(
                                    isBuy
                                        ? "Total Invested"
                                        : "Realized Profit/Loss"
                                )}
                                currencySymbol={currSymbol}
                                previousValue={
                                    isBuy
                                        ? portfolio.previous?.investment || 0
                                        : portfolio.latest?.sellInfo
                                              ?.intialCapitalForPurchase || 0
                                }
                                latestValue={
                                    isBuy
                                        ? portfolio.latest?.investment || 0
                                        : portfolio.latest?.sellInfo
                                              ?.amountToSell || 0
                                }
                                isSell={!isBuy}
                            />
                        </div>
                    </Space>
                </div>
            </AppShell>
        </div>
    );
};

export default Result;
